/**
 * Système de logging centralisé pour l'application
 *
 * En développement: Logs dans la console
 * En production: Envoie uniquement les warnings/errors à Sentry
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.info('Scene', 'Scene initialized successfully');
 *   logger.warn('Compatibility', 'Bulb incompatible with socket', { bulbType, socketType });
 *   logger.error('Texture', 'Failed to load texture', error);
 */

const isDevelopment = import.meta.env.DEV;

/**
 * Formatte un message de log avec catégorie et timestamp
 */
const formatMessage = (category, message) => {
  if (!category) return message;
  const timestamp = new Date().toISOString().split('T')[1].slice(0, 12);
  return `[${timestamp}] [${category}] ${message}`;
};

/**
 * Logger centralisé avec support Sentry
 */
export const logger = {
  /**
   * Info - Logs informatifs (dev only)
   * @param {string} category - Catégorie du log (Scene, Texture, Geometry, etc.)
   * @param {string} message - Message à logger
   * @param {*} data - Données optionnelles à afficher
   */
  info: (category, message, data = null) => {
    if (isDevelopment) {
      const formatted = formatMessage(category, message);
      if (data !== null) {
        console.log(formatted, data);
      } else {
        console.log(formatted);
      }
    }
  },

  /**
   * Log simple (rétrocompatibilité)
   */
  log: (...args) => {
    if (isDevelopment) console.log(...args);
  },

  /**
   * Warning - Avertissements non-critiques
   * @param {string} category - Catégorie du log
   * @param {string} message - Message d'avertissement
   * @param {*} data - Données optionnelles
   */
  warn: (category, message, data = null) => {
    // Rétrocompatibilité: si un seul argument, utiliser l'ancien comportement
    if (message === undefined) {
      if (isDevelopment) console.warn(category);
      return;
    }

    const formatted = formatMessage(category, message);

    if (isDevelopment) {
      if (data !== null) {
        console.warn(formatted, data);
      } else {
        console.warn(formatted);
      }
    }

    // En production: envoyer à Sentry si disponible
    if (!isDevelopment && window.Sentry) {
      window.Sentry.captureMessage(formatted, {
        level: 'warning',
        tags: { category },
        extra: data ? { data } : {},
      });
    }
  },

  /**
   * Error - Erreurs nécessitant attention
   * @param {string} category - Catégorie du log
   * @param {string} message - Message d'erreur
   * @param {Error} error - Objet Error optionnel
   * @param {*} data - Données contextuelles optionnelles
   */
  error: (category, message, error = null, data = null) => {
    // Rétrocompatibilité: si un seul argument, utiliser l'ancien comportement
    if (message === undefined) {
      console.error(category);
      return;
    }

    const formatted = formatMessage(category, message);

    if (isDevelopment) {
      if (error) {
        console.error(formatted, error, data || '');
      } else {
        console.error(formatted, data || '');
      }
    } else {
      // En production: toujours afficher les erreurs
      console.error(formatted);
    }

    // Envoyer à Sentry si disponible
    if (window.Sentry) {
      if (error instanceof Error) {
        window.Sentry.captureException(error, {
          tags: { category },
          contexts: {
            error_context: {
              message: formatted,
              ...(data && { data }),
            },
          },
        });
      } else {
        window.Sentry.captureMessage(formatted, {
          level: 'error',
          tags: { category },
          extra: {
            ...(error && { error }),
            ...(data && { data }),
          },
        });
      }
    }
  },

  /**
   * Debug - Logs de debugging détaillés (dev only)
   * @param {string} category - Catégorie du log
   * @param {string} message - Message de debug
   * @param {*} data - Données à afficher
   */
  debug: (category, message, data = null) => {
    // Rétrocompatibilité: si un seul argument, utiliser l'ancien comportement
    if (message === undefined) {
      if (isDevelopment) console.debug(category);
      return;
    }

    if (isDevelopment) {
      const formatted = formatMessage(category, message);
      if (data !== null) {
        console.debug(formatted, data);
      } else {
        console.debug(formatted);
      }
    }
  },
};

/**
 * Helper pour logger les performances
 * @param {string} category - Catégorie
 * @param {string} operation - Nom de l'opération
 * @returns {Function} Fonction à appeler pour terminer le timing
 */
export const timeOperation = (category, operation) => {
  const start = performance.now();

  return () => {
    const duration = performance.now() - start;
    logger.debug(category, `${operation} completed in ${duration.toFixed(2)}ms`);
  };
};

export default logger;
